<template>
  <article
    class="aula"
    :style="{ '--accent': accent }"
    role="button"
    tabindex="0"
    @click="onOpen"
    @keydown.enter.prevent="onOpen"
    @keydown.space.prevent="onOpen"
  >
    <!-- posicao na sequencia -->
    <span class="aula__order" aria-hidden="true">{{ orderLabel }}</span>

    <!-- capa -->
    <div class="aula__thumb">
      <img
        v-if="item.thumb && !imgError"
        :src="item.thumb"
        :alt="item.title"
        loading="lazy"
        @error="imgError = true"
      />
      <i v-else class="mdi mdi-play-circle aula__thumb-fallback"></i>
    </div>

    <!-- conteudo -->
    <div class="aula__body">
      <h3 class="aula__title">{{ item.title || $t("course.untitled") }}</h3>
      <LessonTagChips
        class="aula__tags"
        :category="item.category"
        :pre="!!item.isPre"
        :optional="!!item.isOptional"
      />
      <p v-if="item.description" class="aula__desc">
        {{ item.description }}
      </p>
    </div>

    <!-- Card 86akptec0: aula de treinamento concluída pelo professor -->
    <span v-if="completed" class="aula__done">
      <i class="mdi mdi-check-circle"></i> {{ $t("course.completedTag") }}
    </span>

    <!-- materiais -->
    <span class="aula__count">
      <i class="mdi mdi-paperclip"></i>
      {{ materialsLabel }}
    </span>

    <!-- abrir -->
    <span class="aula__go" aria-hidden="true">
      <i class="mdi mdi-arrow-right"></i>
    </span>
  </article>
</template>

<script>
import LessonTagChips from "@/components/connect/course/LessonTagChips.vue";

export default {
  name: "LessonRow",
  components: { LessonTagChips },
  props: {
    // Aula (title, description, thumb, content[], order)
    item: {
      type: Object,
      required: true,
    },
    // Cor do tier do modulo - herdada da escada
    accent: {
      type: String,
      default: "#9923CD",
    },
    // Posicao na sequencia (1-based); cai para item.order se ausente
    order: {
      type: Number,
      default: null,
    },
    // Selo de concluída (treinamentos do professor, card 86akptec0)
    completed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      imgError: false,
    };
  },
  computed: {
    orderLabel() {
      const n =
        this.order != null ? this.order : (Number(this.item.order) || 0) + 1;
      return String(n).padStart(2, "0");
    },
    materialsLabel() {
      const n = Array.isArray(this.item.content) ? this.item.content.length : 0;
      return this.$tc("course.materials", n, { count: n });
    },
  },
  watch: {
    item() {
      this.imgError = false;
    },
  },
  methods: {
    onOpen() {
      this.$emit("open", this.item);
    },
  },
};
</script>

<style scoped>
.aula {
  --accent: var(--dk-grape, #9923cd);
  --ink: var(--dk-ink, #211b2e);
  --ink-soft: var(--dk-ink-soft, #6e6a79);
  --ink-faint: var(--dk-faint, #9a96a6);
  --line: var(--dk-line, #eae7f0);
  --line-strong: var(--dk-line-strong, #ded9e8);

  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: var(--dk-surface, #ffffff);
  border: 1px solid var(--line);
  border-radius: 16px;
  cursor: pointer;
  font-family: "Poppins", sans-serif;
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    border-color 0.18s ease;
}
.aula:hover {
  border-color: var(--line-strong);
  box-shadow: 0 18px 34px -26px rgba(33, 27, 46, 0.55);
}
.aula:focus-visible {
  outline: 3px solid var(--accent);
  outline-offset: 2px;
}

/* numeral de ordem */
.aula__order {
  flex: none;
  min-width: 2ch;
  text-align: center;
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 1.9rem;
  line-height: 1;
  letter-spacing: -0.02em;
  color: var(--accent);
  font-variant-numeric: lining-nums tabular-nums;
}

/* capa */
.aula__thumb {
  flex: none;
  position: relative;
  width: 76px;
  height: 56px;
  border-radius: 12px;
  overflow: hidden;
  background: var(--accent);
  display: grid;
  place-items: center;
}
.aula__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.aula__thumb-fallback {
  color: rgba(255, 255, 255, 0.9);
  font-size: 26px;
}

/* conteudo */
.aula__body {
  min-width: 0;
  flex: 1 1 auto;
}
.aula__title {
  margin: 0;
  font-weight: 600;
  font-size: 15px;
  line-height: 1.3;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.aula__tags {
  margin-top: 5px;
}
.aula__desc {
  margin: 3px 0 0;
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--ink-soft);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.aula__done {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 999px;
  background: #e6f6f1;
  font-size: 12px;
  font-weight: 600;
  color: #00805f;
  white-space: nowrap;
}
.aula__done .mdi {
  font-size: 15px;
}

/* materiais */
.aula__count {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  background: #f6f4fa;
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-soft);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.aula__count .mdi {
  font-size: 15px;
  color: var(--ink-faint);
}

/* abrir */
.aula__go {
  flex: none;
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  color: var(--ink-faint);
  transition: color 0.18s ease, transform 0.18s ease;
}
.aula:hover .aula__go {
  color: var(--accent);
  transform: translateX(3px);
}
.aula__go .mdi {
  font-size: 20px;
}

@media (max-width: 640px) {
  .aula__count {
    display: none;
  }
  .aula__desc {
    display: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .aula,
  .aula__go {
    transition: none;
  }
  .aula:hover .aula__go {
    transform: none;
  }
}
</style>
